import { useSyncExternalStore } from 'react';
import type { AuthStateResponse } from '@cloudsign/shared';

/**
 * The server only tells us the login stage in the answer to each login step, and there is no
 * "where am I" call, so we remember the latest answer for this browser tab. If it is lost (reload),
 * the person simply starts at /login again.
 */
const KEY = 'cloudsign.auth';
let state: AuthStateResponse | null = read();
const listeners = new Set<() => void>();

function read(): AuthStateResponse | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as AuthStateResponse) : null;
  } catch {
    return null;
  }
}

export function getAuthState(): AuthStateResponse | null {
  return state;
}

export function setAuthState(next: AuthStateResponse | null): void {
  state = next;
  try {
    if (next) sessionStorage.setItem(KEY, JSON.stringify(next));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage can be unavailable; the in-memory copy still works */
  }
  listeners.forEach((l) => l());
}

export function useAuthState(): AuthStateResponse | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
    () => state,
  );
}

export const STAGE_PATH = {
  mfa: '/login/2fa',
  mfa_setup: '/login/2fa-setup',
  choose_tenant: '/login/choose',
  // Phase 4 (W24 builds the page; ADR 0028 / core-phase4 s2)
  password_change: '/login/password',
  complete: '/',
} as const;

/** Only follow same-site relative paths after sign-in. */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/login')) return '/';
  return next;
}
